// src/hooks/useVisits.ts
import { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  onSnapshot,
  where,
  Timestamp 
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { LandmarkVisit } from '../types/visits';

interface UseVisitsReturn {
  visits: LandmarkVisit[];
  isLoading: boolean;
  error: string | null;
  latestVisit: LandmarkVisit | null;
  clearLatestVisit: () => void;
}

export const useVisits = (): UseVisitsReturn => {
  const [visits, setVisits] = useState<LandmarkVisit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [latestVisit, setLatestVisit] = useState<LandmarkVisit | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const processedVisitIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const user = auth.currentUser;
    
    if (!user) {
      console.log('No user for visits listener');
      setIsLoading(false);
      return;
    }

    // Anonymous users DO have visits — the backend saves them under
    // the anonymous user's uid. They disappear only if the user clears
    // app data or signs up with a different account (unless we upgrade
    // the anonymous account via linkWithCredential, which is a future
    // concern). Never short-circuit here.

    console.log('Setting up visits listener for user:', user.uid, user.isAnonymous ? '(anonymous)' : '');

    // Clean up existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    const visitsRef = collection(db, 'users', user.uid, 'visits');
    const q = query(
      visitsRef,
      orderBy('verified_at', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log('Visits snapshot received, size:', snapshot.size);
        const visitsList: LandmarkVisit[] = [];
        let newVisit: LandmarkVisit | null = null;

        snapshot.forEach((doc) => {
          const data = doc.data();
          
          const visit: LandmarkVisit = {
            id: doc.id,
            landmark_name: data.landmark_name || 'Unknown Landmark',
            landmark_type: data.landmark_type || 'other',
            location: {
              latitude: data.location?.latitude || 0,
              longitude: data.location?.longitude || 0,
            },
            verified_at: data.verified_at instanceof Timestamp 
              ? data.verified_at.toDate() 
              : new Date(data.verified_at || Date.now()),
            confidence_score: data.confidence_score || 0,
            conversation_reference: data.conversation_reference || '',
            verification_method: data.verification_method || 'gps_photo',
            reasoning: data.reasoning || undefined,
            coordinates_match: data.coordinates_match || undefined,
            visit_worthy: data.visit_worthy || undefined,
            photo_reference: data.photo_reference || undefined,
          };

          visitsList.push(visit);

          // Check if this is a new visit (not processed before)
          if (!processedVisitIds.current.has(visit.id)) {
            // Only consider it "new" if it was created very recently (within last 30 seconds)
            const now = new Date();
            const visitAge = now.getTime() - visit.verified_at.getTime();
            
            if (visitAge < 30000) { // 30 seconds
              newVisit = visit;
              console.log('New visit detected:', visit.landmark_name);
            }
            
            processedVisitIds.current.add(visit.id);
          }
        });

        // Sort by verification date (newest first)
        visitsList.sort((a, b) => b.verified_at.getTime() - a.verified_at.getTime());

        setVisits(visitsList);
        setIsLoading(false);

        // Set the latest visit to trigger notification
        if (newVisit) {
          setLatestVisit(newVisit);
        }
      },
      (error) => {
        console.error('Visits listener error:', error);
        setError(error.message);
        setIsLoading(false);
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const clearLatestVisit = () => {
    setLatestVisit(null);
  };

  return {
    visits,
    isLoading,
    error,
    latestVisit,
    clearLatestVisit,
  };
};